import React from 'react';
import {
  Drawer,
  DrawerHeader,
  DrawerBody,
  Button,
  TabList,
  Tab,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  Dismiss24Filled,
  Person24Regular,
  PersonCall24Regular,
  Building24Regular,
} from '@fluentui/react-icons';
import {
  useCustomerForm,
} from '../hooks/useCustomerForm';
// Import the sections directly (not via the ./customerForm barrel): the barrel
// also re-exports ContactsSection, which imports this drawer — going through it
// would re-introduce the CustomerDrawer ↔ ContactsSection module cycle.
import GeneralSection from './customerForm/GeneralSection';
import AddressSection from './customerForm/AddressSection';
import TagsSection from './customerForm/TagsSection';

interface CustomerDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  /** Pre-select the entity kind when opening the create drawer. */
  initialKind?: 'individual' | 'contact' | 'company';
  /** Render an in-drawer Customer / Contact / Company picker at the top of the
   *  body. Use when the drawer is opened from a surface that lacks the
   *  Customers-page Add SplitButton (e.g. the office dashboard quick action). */
  showKindSelector?: boolean;
  /** Restrict which entity kinds the kind selector offers. Defaults to all
   *  three. Surfaces that can't own a Contact pass a narrowed list — e.g. the
   *  order page passes ['individual', 'company'] because a Contact can't own
   *  an order (mirrors the Add-Order action's contact guard in toolbars.tsx). */
  allowedKinds?: Array<'individual' | 'contact' | 'company'>;
  /** Pre-bind the parent company for Contact-add flows opened from a
   *  Company profile. Skips the company re-pick. */
  initialLinkedCompanyId?: string | null;
  initialLinkedCompanyName?: string;
  /** Called after successful save with the resulting customer record */
  onSaved?: (customer: any) => void;
}

const useStyles = makeStyles({
  drawer: {
    // Strip the default border-left that Fluent's overlay drawer paints
    // between the surface and the page behind it. Applied to both the outer
    // wrapper and any nested surface element since Fluent's structure
    // varies by drawer type.
    borderLeftStyle: 'none',
    borderLeftWidth: 0,
    '&::before, &::after': {
      borderLeftStyle: 'none',
      borderLeftWidth: 0,
    },
    '& > *': {
      borderLeftStyle: 'none',
      borderLeftWidth: 0,
    },
    // Below 768px the medium drawer (~592px wide) overflows a phone
    // viewport. Force it to full-screen via !important - Fluent's
    // drawer size is set inline by the component so a regular rule
    // wouldn't win.
    '@media (max-width: 768px)': {
      width: '100vw !important',
      maxWidth: '100vw !important',
    },
  },
  drawerHeader: {
    // Brand gradient — colors mirrored from website/src/app/globals.css
    // (--navy → --teal). The frontend (admin) doesn't have these as Fluent
    // tokens, so they're hardcoded here. If they ever land in a shared theme,
    // swap to the token references.
    backgroundImage: 'linear-gradient(to right, #00a6bc 0%, #00bba8 100%)',
    color: '#FFFFFF',
    // Override Fluent's default DrawerHeader padding (asymmetric top/bottom)
    // with a symmetric value so our centered title row actually appears
    // visually centered against the gradient band.
    paddingTop: '20px',
    paddingBottom: '20px',
    paddingLeft: '24px',
    paddingRight: '12px',
    // Subtle icon buttons set their own color — override so they're visible
    // against the gradient.
    '& button': {
      color: '#FFFFFF',
    },
    '& button:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
      color: '#FFFFFF',
    },
  },
  // Custom title row replaces Fluent's DrawerHeaderTitle so we can guarantee
  // vertical centering without fighting the component's internal margins,
  // line-heights, and h2 wrapping. Fixed height matches the icon-button
  // bounding box so the title text and X land on the same centerline.
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.gap('12px'),
    width: '100%',
    height: '32px',
  },
  titleText: {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: '20px',
    fontWeight: 600,
    lineHeight: '32px',
    margin: 0,
    padding: 0,
  },
  drawerBody: {
    ...shorthands.padding('0'),
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
  },
  kindSelector: {
    ...shorthands.padding('12px', '24px', '0', '24px'),
    flexShrink: 0,
  },
  scrollSurface: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    ...shorthands.padding('16px', '24px'),
    ...shorthands.gap('16px'),
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    ...shorthands.gap('8px'),
    ...shorthands.padding('16px', '24px'),
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
    backgroundColor: 'transparent',
    flexShrink: 0,
  },
  headerActionGroup: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('4px'),
  },
});

/**
 * Drawer for creating a new customer record (individual / contact / company).
 * Slides in from the right. Editing happens on the full profile page
 * (`/customers/:id`), not here — this drawer is add-only.
 *
 * Commits only on the explicit Create button (which closes via onSaved →
 * onClose). The Close button confirms before discarding typed-but-unsaved data.
 */
const CustomerDrawer: React.FC<CustomerDrawerProps> = ({
  isOpen,
  onClose,
  initialKind,
  showKindSelector,
  allowedKinds = ['individual', 'contact', 'company'],
  initialLinkedCompanyId,
  initialLinkedCompanyName,
  onSaved,
}) => {
  const styles = useStyles();

  // Kind selector tabs, filtered to the kinds this surface allows. Order
  // (Customer / Contact / Company) is preserved regardless of which subset
  // is shown.
  const KIND_TABS = [
    { value: 'individual' as const, label: 'Customer', icon: <Person24Regular /> },
    { value: 'contact' as const, label: 'Contact', icon: <PersonCall24Regular /> },
    { value: 'company' as const, label: 'Company', icon: <Building24Regular /> },
  ].filter((t) => allowedKinds.includes(t.value));

  const { title, bag, headerContent, actions, isDirty, saving, customerKind, handleCustomerKindChange } = useCustomerForm({
    variant: 'add',
    initialKind,
    initialLinkedCompanyId,
    initialLinkedCompanyName,
    isOpen,
    onClose,
    onSaved,
  });

  const handleClose = React.useCallback(() => {
    // Create already clicked and the save is in flight: don't prompt to
    // discard. Ignore the click-away and let the save resolve — on success the
    // hook calls onClose itself; on failure the drawer stays open so the error
    // is visible. Either way dismissal is deferred until the save completes.
    if (saving) return;
    // Otherwise confirm if the user has typed data they're about to lose. Add
    // only commits on the explicit Create button, so there's no save to flush.
    if (isDirty) {
      const ok = window.confirm(
        'Discard new customer? Any data you entered will be lost.'
      );
      if (!ok) return;
    }
    onClose();
  }, [saving, isDirty, onClose]);

  return (
    <Drawer
      className={styles.drawer}
      type="overlay"
      position="end"
      size="medium"
      open={isOpen}
      onOpenChange={(_, { open }) => { if (!open) handleClose(); }}
      modalType="modal"
    >
      <DrawerHeader className={styles.drawerHeader}>
        <div className={styles.titleRow}>
          <h2 className={styles.titleText}>{title}</h2>
          <div className={styles.headerActionGroup}>
            <Button
              appearance="subtle"
              icon={<Dismiss24Filled />}
              aria-label="Close"
              onClick={handleClose}
            />
          </div>
        </div>
      </DrawerHeader>

      <DrawerBody className={styles.drawerBody}>
        {showKindSelector && (
          <div className={styles.kindSelector}>
            <TabList
              selectedValue={customerKind}
              onTabSelect={(_, data) =>
                handleCustomerKindChange(data.value as 'individual' | 'contact' | 'company')
              }
            >
              {KIND_TABS.map((t) => (
                <Tab key={t.value} value={t.value} icon={t.icon}>{t.label}</Tab>
              ))}
            </TabList>
          </div>
        )}
        {headerContent}
        {/* Curated add-mode subset: identity + address + tags, collected up
            front so a new record gets categorized at creation (Booqable accepts
            tag_list on the create payload). Contacts skip address/tags. */}
        <div className={styles.scrollSurface}>
          <GeneralSection form={bag} />
          {customerKind !== 'contact' && <AddressSection form={bag} />}
          {customerKind !== 'contact' && <TagsSection form={bag} />}
        </div>
        <div className={styles.footer}>{actions}</div>
      </DrawerBody>
    </Drawer>
  );
};

export default CustomerDrawer;
